import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { ArrowUpRight, Bot, Sparkles, Workflow, Zap } from "lucide-react";
import { NIVA_NAV_URL } from "@/common/niva.config";

const NIVA_HIGHLIGHTS = [
  {
    icon: Bot,
    label: "250+ industry personas for real business roles",
  },
  {
    icon: Workflow,
    label: "Workflows, memory, and knowledge in one platform",
  },
  {
    icon: Zap,
    label: "Embed on any site in minutes, no long engineering cycle",
  },
];

export function NivaNavPill() {
  const [open, setOpen] = useState(false);

  return (
    <div
      className="relative hidden shrink-0 md:block"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocusCapture={() => setOpen(true)}
      onBlurCapture={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false);
      }}
    >
      <Link
        href={NIVA_NAV_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Try NIVA, agentic AI chatbot platform"
        className="niva-nav-pill group/pill relative inline-flex items-center gap-2 rounded-full border border-[#1c8c80]/35 bg-gradient-to-r from-muted to-surface px-3 py-1.5 text-[13px] font-semibold text-ink shadow-[0_8px_24px_-14px_rgba(28,140,128,0.45)] transition-all duration-300 hover:border-[#1c8c80]/70"
      >
        <span
          aria-hidden
          className="niva-nav-pill-glow absolute inset-0 rounded-full opacity-40"
        />
        <span className="relative flex h-7 w-7 items-center justify-center overflow-hidden rounded-full border border-line bg-surface shadow-sm">
          <Image
            src="/icons/niva_logo.svg"
            alt=""
            width={22}
            height={22}
            className="niva-mark h-[18px] w-[18px] object-contain"
          />
        </span>
        <span className="relative flex flex-col items-start leading-tight">
          <span className="flex items-center gap-1">
            Try NIVA
            <Sparkles
              size={14}
              strokeWidth={2}
              className="text-[#1c8c80] opacity-80 transition-opacity group-hover/pill:opacity-100"
              aria-hidden
            />
          </span>
          <span className="text-[10px] font-medium uppercase tracking-[0.14em] text-ink-soft">
            Agentic AI
          </span>
        </span>
      </Link>

      <div
        className={`absolute top-full right-0 z-[70] w-[min(100vw-2rem,320px)] pt-2 ${
          open ? "" : "pointer-events-none"
        }`}
      >
        <div
          className={`niva-nav-card rounded-xl border border-line bg-surface p-4 shadow-[0_24px_64px_-20px_rgba(15,28,26,0.35)] transition-all duration-200 origin-top-right ${
            open
              ? "visible translate-y-0 opacity-100"
              : "invisible -translate-y-1 opacity-0"
          }`}
          role="region"
          aria-label="About NIVA"
          aria-hidden={!open}
        >
          <p className="text-[15px] font-semibold leading-snug text-ink">
            AI chatbots built for real workflows
          </p>
          <p className="mt-1.5 text-[12.5px] leading-relaxed text-ink-muted">
            Beyond scripted replies: personas, flows, and integrations in one
            white-label platform.
          </p>
          <ul className="mt-3 space-y-2">
            {NIVA_HIGHLIGHTS.map(({ icon: Icon, label }) => (
              <li
                key={label}
                className="flex items-start gap-2 text-[12px] leading-snug text-ink-muted"
              >
                <Icon
                  size={15}
                  strokeWidth={1.6}
                  className="mt-0.5 shrink-0 text-[#1c8c80]"
                  aria-hidden
                />
                {label}
              </li>
            ))}
          </ul>
          <Link
            href={NIVA_NAV_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 flex w-full items-center justify-center gap-1.5 rounded-lg bg-[#1c8c80] py-2.5 text-[13px] font-semibold text-white transition-colors hover:bg-[#157a6f]"
          >
            Explore NIVA
            <ArrowUpRight size={15} strokeWidth={2} aria-hidden />
          </Link>
        </div>
      </div>
    </div>
  );
}

export function NivaMobileCard({ onNavigate }) {
  return (
    <Link
      href={NIVA_NAV_URL}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onNavigate}
      aria-label="Try NIVA: Agentic AI chatbot platform"
      className="niva-mobile-card mx-1 mb-3 block rounded-2xl border border-[#1c8c80]/30 bg-gradient-to-br from-muted via-surface to-surface p-4 transition-colors active:bg-muted"
    >
      <div className="flex items-start gap-3">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-line bg-surface shadow-sm">
          <Image
            src="/icons/niva_logo.svg"
            alt=""
            width={28}
            height={28}
            className="niva-mark h-7 w-7 object-contain"
          />
        </span>
        <div className="min-w-0 flex-1">
          <span className="inline-flex items-center gap-1 rounded-full bg-[#1c8c80]/12 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-[#1c8c80]">
            <Sparkles size={10} aria-hidden />
            Our product
          </span>
          <p className="mt-1.5 text-base font-bold text-ink">Try NIVA</p>
          <p className="mt-1 text-[13px] leading-snug text-ink-muted">
            Agentic chatbots with personas, workflows, and no-code setup.
          </p>
        </div>
        <ArrowUpRight
          size={18}
          className="mt-1 shrink-0 text-[#1c8c80]"
          aria-hidden
        />
      </div>
    </Link>
  );
}
